<template>
	<div
		class="g-block-pay-video-series"
		:class="{
			'g-block-pay-video-series--edit': editable,
			'is-active': editable && active,
			'g-block-pay-video-series--h5': h5,
			'g-block-pay-video-series--compact': compact,
			'g-block-pay-video-series--unlocked': isUnlocked,
			'is-playing': isPlaying,
		}"
		@click="onWrapClick"
	>
		<div class="g-block-pay-video-series__card">
			<div class="g-block-pay-video-series__player">
				<img
					v-if="activeCoverUrl && !hasStartedPlay"
					class="g-block-pay-video-series__cover"
					:src="activeCoverUrl"
					alt=""
				/>
				<video
					v-if="playingUrl"
					ref="videoEl"
					class="g-block-pay-video-series__video"
					:class="{ 'is-visible': hasStartedPlay }"
					:src="playingUrl"
					:controls="hasStartedPlay"
					playsinline
					webkit-playsinline
					preload="metadata"
					@play="onVideoPlay"
					@pause="onVideoPause"
					@ended="onVideoEnded"
				></video>
				<template v-if="!hasStartedPlay">
					<div class="g-block-pay-video-series__player-mask"></div>
					<div class="g-block-pay-video-series__player-ui">
						<div class="g-block-pay-video-series__player-top">
							<span class="g-block-pay-video-series__player-title">
								{{ activeEpisodeLabel }}
							</span>
						</div>
						<button
							type="button"
							class="g-block-pay-video-series__play-btn"
							aria-label="播放"
							@click.stop="onPlayButtonClick"
						>
							<span class="g-block-pay-video-series__triangle"></span>
						</button>
					</div>
				</template>
			</div>

			<div class="g-block-pay-video-series__scroll">
				<div class="g-block-pay-video-series__info">
					<div class="g-block-pay-video-series__title">{{ seriesData.title }}</div>
					<div class="g-block-pay-video-series__meta">{{ metaText }}</div>
					<div v-if="seriesData.description" class="g-block-pay-video-series__desc">
						{{ seriesData.description }}
					</div>
				</div>

				<div class="g-block-pay-video-series__list">
					<div
						v-for="entry in listEntries"
						:key="entry.key"
						class="g-block-pay-video-series__episode"
						:class="{
							'is-current': entry.index === activeIndex && !entry.locked,
							'is-locked': entry.locked,
						}"
						@click="onEpisodeClick(entry, $event)"
					>
						<span class="g-block-pay-video-series__episode-index">
							{{ formatEpisodeIndex(entry.index) }}
						</span>
						<div class="g-block-pay-video-series__episode-main">
							<div class="g-block-pay-video-series__episode-title">{{ entry.item.title }}</div>
							<div
								v-if="entry.locked"
								class="g-block-pay-video-series__episode-sub"
							>解锁后观看</div>
						</div>
						<template v-if="entry.locked">
							<i class="mbm-iconfont mbm-suoding g-block-pay-video-series__lock"></i>
						</template>
						<template v-else-if="entry.index === activeIndex && isPlaying">
							<div class="g-block-pay-video-series__playing">
								<span class="g-block-pay-video-series__wave" aria-hidden="true">
									<span></span><span></span><span></span>
								</span>
								<span>播放中</span>
							</div>
						</template>
						<template v-else>
							<span class="g-block-pay-video-series__duration">{{ getDurationText(entry.item) }}</span>
						</template>
					</div>
				</div>
			</div>

			<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-video-series__footer">
				<div class="g-block-pay-video-series__footer-main">
					<div class="g-block-pay-video-series__unlock-label">{{ unlockLabel }}</div>
					<div class="g-block-pay-video-series__footer-price-row">
						<span class="g-block-pay-video-series__price">
							<span class="g-block-pay-video-series__currency">{{ seriesData.currency }}</span>
							<span class="g-block-pay-video-series__price-value">{{ seriesData.price }}</span>
						</span>
						<span class="g-block-pay-video-series__pay-desc">{{ seriesData.payDesc }}</span>
					</div>
				</div>
				<button
					type="button"
					class="g-block-pay-video-series__unlock-btn"
					:style="{ background: seriesData.buttonBg }"
					@click.stop="onUnlockClick"
				>{{ seriesData.buttonText }}</button>
			</div>
		</div>
	</div>
</template>

<script>
	import baseFunc from '../../../../config/base_func.js';
	import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
	import {
		formatEpisodeIndex,
		getPayVideoSeriesListEntries,
		getVideoListCoverUrl,
		getVideoListVideoUrl,
		isPayVideoSeriesUnlocked,
		normalizePayVideoSeriesData,
		setPayVideoSeriesUnlocked,
	} from '../../../utils/payVideoSeries.js';

	export default {
		name: 'GPayVideoSeriesShell',
		mixins: [geditPayUnlockMixin],
		props: {
			data: {
				type: Object,
				required: true,
			},
			blockId: {
				type: String,
				default: '',
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			idKey: {
				type: String,
				default: '',
			},
			compact: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				unlockedLocal: false,
				activeIndex: 0,
				isPlaying: false,
				hasStartedPlay: false,
				playingUrl: '',
				permissionRequests: {},
			};
		},
		computed: {
			seriesData() {
				return normalizePayVideoSeriesData(this.data);
			},
			listEntries() {
				return getPayVideoSeriesListEntries(this.seriesData, {
					unlocked: this.isUnlocked,
					compact: this.compact,
				});
			},
			totalCount() {
				return this.seriesData.items.length;
			},
			isUnlocked() {
				if (this.editable) return false;
				return this.unlockedLocal || isPayVideoSeriesUnlocked(this.blockId) || this.isGeditModulePaid;
			},
			activeEntry() {
				return this.listEntries.find(entry => entry.index === this.activeIndex)
					|| this.listEntries[0]
					|| null;
			},
			activeItem() {
				return this.activeEntry?.item || this.seriesData.items[this.activeIndex] || null;
			},
			activeCoverUrl() {
				return this.activeItem ? getVideoListCoverUrl(this.activeItem) : '';
			},
			activeEpisodeLabel() {
				if (!this.activeItem) return '';
				return `${formatEpisodeIndex(this.activeIndex)} ${this.activeItem.title || ''}`.trim();
			},
			metaText() {
				const subtitle = this.seriesData.subtitle || '';
				
				return subtitle;
			},
			unlockLabel() {
				return `解锁全${this.totalCount}集`;
			},
		},
		watch: {
			blockId: {
				immediate: true,
				handler() {
					this.syncUnlockState();
				},
			},
			'listEntries.length'(len) {
				if (this.activeIndex >= len) {
					this.activeIndex = Math.max(0, len - 1);
					this.stopPlay();
				}
			},
		},
		beforeUnmount() {
			this.stopPlay();
		},
		methods: {
			formatEpisodeIndex,
			syncUnlockState() {
				this.unlockedLocal = isPayVideoSeriesUnlocked(this.blockId);
			},
			getDurationText(item) {
				return item.video?.durationText || '00:00';
			},
			isEntryPlayable(entry) {
				if (!(this.editable || this.h5)) return false;
				return this.editable || !entry.locked;
			},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
			onUnlockClick() {
				this.runGeditUnlockFlow({
					applyUnlock: () => {
						setPayVideoSeriesUnlocked(this.blockId, true);
						this.unlockedLocal = true;
					},
				});
			},
			onEpisodeClick(entry, event) {
				if (!this.editable) {
					event.stopPropagation();
				}
				if (entry.locked) {
					if (!this.editable) {
						this.onUnlockClick();
					}
					return;
				}
				if (this.activeIndex === entry.index && this.isPlaying) {
					this.pausePlay();
					return;
				}
				if (this.activeIndex === entry.index && this.playingUrl && !this.isPlaying) {
					this.resumePlay();
					return;
				}
				if (this.activeIndex !== entry.index) {
					this.resetPlaybackState();
					this.activeIndex = entry.index;
				}
				if (!this.isEntryPlayable(entry)) return;
				this.playEntry(entry);
			},
			onPlayButtonClick() {
				if (!this.activeEntry) return;
				if (this.activeEntry.locked) {
					if (!this.editable) this.onUnlockClick();
					return;
				}
				if (!this.isEntryPlayable(this.activeEntry)) return;
				if (this.isPlaying) {
					this.pausePlay();
					return;
				}
				if (this.playingUrl) {
					this.resumePlay();
					return;
				}
				this.playEntry(this.activeEntry);
			},
			playEntry(entry) {
				const url = getVideoListVideoUrl(entry.item);
				if (!url) return;

				// 同一集已加载：只续播，不要重设 src（否则会从头开始）
				if (this.playingUrl === url && this.$refs.videoEl) {
					this.resumePlay();
					return;
				}

				this.hasStartedPlay = false;
				const entryKey = entry.key;

				if (this.h5 && entry.item.video?.id && !this.permissionRequests[entryKey]) {
					this.permissionRequests[entryKey] = true;
					this.requestPlayPermission(entry, url).then(() => {
						this.$nextTick(() => {
							this.$refs.videoEl?.play?.().catch(() => {});
						});
					}).catch((err) => {
						this.permissionRequests[entryKey] = false;
						this.$message?.error?.(err.data?.message || '请求失败');
					});
					return;
				}
				this.playingUrl = url;
				this.$nextTick(() => {
					this.$refs.videoEl?.play?.().catch(() => {});
				});
			},
			getQueryStringByChannel(name) {
				const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`, 'i');
				if (!window.location.href.split('?')[1]) return null;
				const re = window.location.href.split('?')[1].match(reg);
				if (re != null) return unescape(re[2]);
				return null;
			},
			requestPlayPermission(entry, fallbackUrl) {
				return this.$apiRequest.post('/api/getMediaPlayPermissions', {
					attach_id: entry.item.video.id,
					id_key: this.idKey,
					unique: '',
					app_type:1,
					p_key: this.getQueryStringByChannel('p_key') == null ? '' : this.getQueryStringByChannel('p_key'),
				}).then((res) => {
					if (res.data?.play_url) {
						this.playingUrl = baseFunc.getQiniusign(res.data.play_url);
					} else {
						this.playingUrl = fallbackUrl;
					}
				});
			},
			resumePlay() {
				const el = this.$refs.videoEl;
				if (!el) {
					if (this.activeEntry) this.playEntry(this.activeEntry);
					return;
				}
				el.play?.().catch(() => {});
			},
			pausePlay() {
				this.$refs.videoEl?.pause?.();
				this.isPlaying = false;
			},
			onVideoPlay() {
				this.isPlaying = true;
				this.hasStartedPlay = true;
			},
			onVideoPause() {
				this.isPlaying = false;
			},
			onVideoEnded() {
				this.isPlaying = false;
			},
			resetPlaybackState() {
				const el = this.$refs.videoEl;
				if (el) el.pause();
				this.playingUrl = '';
				this.isPlaying = false;
				this.hasStartedPlay = false;
			},
			stopPlay() {
				this.resetPlaybackState();
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-pay-video-series {
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 12px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-pay-video-series__card {
		display: flex;
		flex-direction: column;
		overflow: hidden;
		background: #fff;
		border-radius: 12px;
		box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-video-series__player {
		position: relative;
		flex-shrink: 0;
		width: 100%;
		aspect-ratio: 16 / 9;
		background: #111;
		overflow: hidden;
	}

	.g-block-pay-video-series__cover,
	.g-block-pay-video-series__video {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.g-block-pay-video-series__video {
		opacity: 0;
		pointer-events: none;

		&.is-visible {
			opacity: 1;
			pointer-events: auto;
			z-index: 1;
		}
	}

	.g-block-pay-video-series__player-mask {
		position: absolute;
		inset: 0;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0.35) 0%, transparent 28%, transparent 70%, rgba(0, 0, 0, 0.25) 100%);
		z-index: 2;
	}

	.g-block-pay-video-series__player-ui {
		position: absolute;
		inset: 0;
		z-index: 3;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.g-block-pay-video-series__player-top {
		position: absolute;
		left: 12px;
		top: 12px;
		right: 12px;
	}

	.g-block-pay-video-series__player-title {
		display: inline-block;
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 13px;
		font-weight: 600;
		color: #fff;
		text-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
	}

	.g-block-pay-video-series__play-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 52px;
		height: 52px;
		border: none;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.45);
		cursor: pointer;
		padding: 0;
	}

	.g-block-pay-video-series__triangle {
		width: 0;
		height: 0;
		margin-left: 3px;
		border-style: solid;
		border-width: 8px 0 8px 14px;
		border-color: transparent transparent transparent #fff;
	}

	.g-block-pay-video-series__pause {
		position: relative;
		width: 14px;
		height: 16px;

		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 0;
			width: 4px;
			height: 100%;
			border-radius: 1px;
			background: #fff;
		}

		&::before { left: 0; }
		&::after { right: 0; }
	}

	.g-block-pay-video-series__scroll {
		flex: 1;
		min-height: 0;
		max-height: 350px;
		overflow-x: hidden;
		overflow-y: auto;
		padding: 14px 12px 8px;
		scrollbar-width: none;
		-ms-overflow-style: none;

		&::-webkit-scrollbar {
			display: none;
			width: 0;
			height: 0;
		}
	}

	.g-block-pay-video-series__title {
		font-size: 18px;
		font-weight: 700;
		color: #1a1a1a;
		line-height: 1.35;
	}

	.g-block-pay-video-series__meta {
		margin-top: 6px;
		font-size: 13px;
		color: #8a8f99;
		line-height: 1.4;
	}

	.g-block-pay-video-series__desc {
		margin-top: 8px;
		font-size: 12px;
		color: #a0a5b0;
		line-height: 1.5;
	}

	.g-block-pay-video-series__list {
		display: flex;
		flex-direction: column;
		gap: 10px;
		margin-top: 14px;
	}

	.g-block-pay-video-series__episode {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px;
		border: 1px solid #eceff3;
		border-radius: 12px;
		background: #fff;
		box-sizing: border-box;
		cursor: pointer;
		transition: border-color 0.2s, background 0.2s;

		&.is-current {
			border-color: #7aa0ff;
			background: #f3f7ff;
		}

		&.is-locked {
			cursor: pointer;
		}
	}

	.g-block-pay-video-series__episode-index {
		flex-shrink: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		border-radius: 50%;
		font-size: 14px;
		font-weight: 700;
		color: #666;
		line-height: 1;
		border: 1px solid #eceff3;
		.is-current & {
			color: #285af9;
			border: 1px solid #285af9;
		}
	}

	.g-block-pay-video-series__episode-main {
		flex: 1;
		min-width: 0;
	}

	.g-block-pay-video-series__episode-title {
		font-size: 14px;
		font-weight: 600;
		color: #1a1a1a;
		line-height: 1.35;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.g-block-pay-video-series__episode-sub {
		margin-top: 4px;
		font-size: 12px;
		color: #999;
		line-height: 1.3;
	}

	.g-block-pay-video-series__duration {
		flex-shrink: 0;
		font-size: 12px;
		color: #999;
	}

	.g-block-pay-video-series__lock {
		flex-shrink: 0;
		font-size: 18px;
		color: #666;
	}

	.g-block-pay-video-series__playing {
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2px;
		font-size: 11px;
		color: #285af9;
		line-height: 1.2;
	}

	.g-block-pay-video-series__wave {
		display: inline-flex;
		align-items: flex-end;
		gap: 2px;
		height: 14px;

		span {
			width: 2px;
			border-radius: 1px;
			background: #285af9;
			animation: g-pay-video-series-wave 0.9s ease-in-out infinite;

			&:nth-child(1) { height: 6px; animation-delay: 0s; }
			&:nth-child(2) { height: 12px; animation-delay: 0.15s; }
			&:nth-child(3) { height: 8px; animation-delay: 0.3s; }
		}
	}

	@keyframes g-pay-video-series-wave {
		0%, 100% { transform: scaleY(0.55); }
		50% { transform: scaleY(1); }
	}

	.g-block-pay-video-series__footer {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 12px 14px 14px;
		border-top: 1px solid #f0f2f5;
		background: #fff;
	}

	.g-block-pay-video-series__footer-main {
		min-width: 0;
		flex: 1;
	}

	.g-block-pay-video-series__unlock-label {
		font-size: 12px;
		color: #999;
		line-height: 1.3;
	}

	.g-block-pay-video-series__footer-price-row {
		display: flex;
		align-items: baseline;
		gap: 8px;
		margin-top: 4px;
	}

	.g-block-pay-video-series__price {
		display: inline-flex;
		align-items: baseline;
		color: #ff4d4f;
		line-height: 1;
	}

	.g-block-pay-video-series__currency {
		font-size: 14px;
		font-weight: 600;
		margin-right: 1px;
	}

	.g-block-pay-video-series__price-value {
		font-size: 24px;
		font-weight: 700;
	}

	.g-block-pay-video-series__pay-desc {
		font-size: 12px;
		color: #999;
		white-space: nowrap;
	}

	.g-block-pay-video-series__unlock-btn {
		flex-shrink: 0;
		border: none;
		border-radius: 10px;
		padding: 12px 18px;
		font-size: 14px;
		font-weight: 600;
		color: #fff;
		cursor: pointer;
		white-space: nowrap;
		line-height: 1;
	}

	.g-block-pay-video-series--compact {
		.g-block-pay-video-series__scroll {
			max-height: 180px;
		}
	}

	.g-block-pay-video-series--h5 {
		.g-block-pay-video-series__card {
			border-radius: 0.24rem;
			box-shadow: 0 0 0.24rem rgba(0, 0, 0, 0.1);
		}

		.g-block-pay-video-series__player-top {
			left: 0.24rem;
			top: 0.24rem;
			right: 0.24rem;
		}

		.g-block-pay-video-series__player-title {
			font-size: 0.26rem;
		}

		.g-block-pay-video-series__play-btn {
			width: 1.04rem;
			height: 1.04rem;
		}

		.g-block-pay-video-series__triangle {
			border-width: 0.16rem 0 0.16rem 0.28rem;
			margin-left: 0.06rem;
		}

		.g-block-pay-video-series__scroll {
			max-height: 6.4rem;
			padding: 0.28rem 0.24rem 0.16rem;
		}

		.g-block-pay-video-series__title {
			font-size: 0.36rem;
		}

		.g-block-pay-video-series__meta {
			margin-top: 0.12rem;
			font-size: 0.26rem;
		}

		.g-block-pay-video-series__desc {
			margin-top: 0.16rem;
			font-size: 0.24rem;
		}

		.g-block-pay-video-series__list {
			gap: 0.2rem;
			margin-top: 0.28rem;
		}

		.g-block-pay-video-series__episode {
			gap: 0.24rem;
			padding: 0.24rem;
			border-radius: 0.24rem;
		}

		.g-block-pay-video-series__episode-index {
			width: 0.72rem;
			height: 0.72rem;
			font-size: 0.28rem;
		}

		.g-block-pay-video-series__episode-title {
			font-size: 0.28rem;
		}

		.g-block-pay-video-series__episode-sub,
		.g-block-pay-video-series__duration {
			font-size: 0.24rem;
		}

		.g-block-pay-video-series__lock {
			font-size: 0.36rem;
		}

		.g-block-pay-video-series__playing {
			font-size: 0.22rem;
		}

		.g-block-pay-video-series__footer {
			gap: 0.24rem;
			padding: 0.24rem 0.28rem 0.28rem;
		}

		.g-block-pay-video-series__unlock-label {
			font-size: 0.24rem;
		}

		.g-block-pay-video-series__footer-price-row {
			gap: 0.16rem;
			margin-top: 0.08rem;
		}

		.g-block-pay-video-series__currency {
			font-size: 0.28rem;
		}

		.g-block-pay-video-series__price-value {
			font-size: 0.48rem;
		}

		.g-block-pay-video-series__pay-desc {
			font-size: 0.24rem;
		}

		.g-block-pay-video-series__unlock-btn {
			border-radius: 0.2rem;
			padding: 0.24rem 0.36rem;
			font-size: 0.28rem;
		}
	}
</style>
